import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Pagination,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import api from '../api/client';
import { getModernwashLegacySales } from '../api/modernwashLegacy';
import { actionBtnSx, tableTagSx } from '../styles/uiTokens';

// 오늘 날짜 → YYYY-MM-DD 형식
function toDateStr(date) {
  return date.toISOString().slice(0, 10);
}

// ModernWash 매출 row 출처 라벨 — backend SalesRow.source 와 동기화
// external: 세차 연동 결제 / internal: 자체 키오스크 결제
const SOURCE_LABELS = {
  external: '세차 연동',
  internal: '키오스크',
};
const SOURCE_COLORS = {
  external: 'default',
  internal: 'primary',
};

// 카테고리는 서로 겹칠 수 있는 조회 관점이다. 예: 키오스크 RF 결제는 키오스크와 RF카드 양쪽에 보인다.
// 수동 지급은 결제가 아니므로 "이용권" 매출에 포함하지 않는다.
const SALES_CATEGORIES = [
  { key: '', label: '전체' },
  { key: 'kiosk', label: '키오스크' },
  { key: 'rf', label: 'RF 카드' },
  { key: 'app', label: '앱 결제' },
  { key: 'cash', label: '현금' },
  { key: 'pass', label: '이용권' },
];

const SalesPage = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toDateStr(firstOfMonth));
  const [endDate, setEndDate] = useState(toDateStr(today));
  const [payMethod, setPayMethod] = useState('');
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, list: [], summary: {}, categorySummary: {} });
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState('mogoo');
  const isLegacySource = dataSource === 'modernwash';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate, endDate, payMethod: payMethod || undefined, productName: productName || undefined,
        category: category || undefined, page, limit: 50,
      };
      if (isLegacySource) {
        const legacy = await getModernwashLegacySales(params);
        setData(legacy.data || { total: 0, list: [], summary: {}, categorySummary: {} });
      } else {
        const res = await api.get('/sales', { params });
        setData(res.data);
      }
    } catch (err) {
      console.error('매출 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, payMethod, productName, category, page, isLegacySource]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handleCategoryChange = (nextCategory) => {
    setCategory(nextCategory);
    setPage(1);
  };

  const handleDataSourceChange = (_, nextSource) => {
    if (!nextSource) return;
    setDataSource(nextSource);
    setPage(1);
  };

  const handleDownload = async () => {
    try {
      const params = {
        startDate, endDate, payMethod: payMethod || undefined, productName: productName || undefined,
        category: category || undefined, page: 1, limit: 200,
      };
      const result = isLegacySource
        ? await getModernwashLegacySales(params)
        : (await api.get('/sales', { params })).data;
      const sourceData = isLegacySource ? result.data : result;
      const rows = (sourceData.list || []).map((r) => ({
        '출처': SOURCE_LABELS[r.source] ?? '외주',
        '날짜/시간': new Date(r.date).toLocaleString('ko-KR'),
        '번호판': r.lp,
        '상품명': r.productName,
        '결제수단': r.payMethod,
        '카드사': r.issuer,
        '위치': r.location,
        '금액': r.amount,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '매출내역');
      XLSX.writeFile(wb, `매출_${startDate}_${endDate}.xlsx`);
    } catch (err) {
      alert('엑셀 다운로드에 실패했습니다.');
    }
  };

  const formatAmount = (n) => n?.toLocaleString('ko-KR') + '원';

  const payMethodColor = (method) => {
    if (method === '신용카드') return 'primary';
    if (method === 'RF카드')   return 'warning';
    if (method === '앱 결제')  return 'success';
    if (method === '현금')     return 'secondary';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        매출 관리
      </Typography>

      <ToggleButtonGroup exclusive value={dataSource} onChange={handleDataSourceChange} size="small" color="primary" sx={{ mb: 2 }}>
        <ToggleButton value="mogoo">모구 매출</ToggleButton>
        <ToggleButton value="modernwash">ModernWash 전주점 기존 매출</ToggleButton>
      </ToggleButtonGroup>
      {isLegacySource && (
        <Typography variant="body2" color="info.main" sx={{ mb: 2 }}>
          ModernWash 전주점 기존 데이터 · 읽기 전용
        </Typography>
      )}

      {/* 필터 — 모바일에선 필드/버튼 각각 자연 폭 + flexWrap으로 줄바꿈 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            label="시작일"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: '48%', sm: 160 } }}
          />
          <TextField
            label="종료일"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: '48%', sm: 160 } }}
          />
          <FormControl size="small" sx={{ width: { xs: '48%', sm: 130 } }}>
            <InputLabel>결제수단</InputLabel>
            <Select value={payMethod} label="결제수단" onChange={(e) => { setPayMethod(e.target.value); setPage(1); }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="신용카드">신용카드</MenuItem>
              <MenuItem value="RF카드">RF카드</MenuItem>
              <MenuItem value="앱 결제">앱 결제</MenuItem>
              <MenuItem value="현금">현금</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: { xs: '48%', sm: 140 } }}>
            <InputLabel>상품명</InputLabel>
            <Select value={productName} label="상품명" onChange={(e) => { setProductName(e.target.value); setPage(1); }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="올인원 1회권">올인원 1회권</MenuItem>
              <MenuItem value="데일리 세차">데일리 세차</MenuItem>
              <MenuItem value="올인원 세차">올인원 세차</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ ...actionBtnSx, minWidth: 'auto', px: 2.5 }}
          >
            조회
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ ...actionBtnSx, minWidth: 'auto', px: 2 }}
          >
            엑셀 다운로드
          </Button>
        </Box>
      </Paper>

      {/* 요약 카드 — 모바일에선 폰트 작게 + nowrap */}
      <Stack
        direction="row"
        spacing={{ xs: 1, md: 2 }}
        sx={{ mb: 2 }}
      >
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">조회 건수</Typography>
          <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {data.summary?.count?.toLocaleString() ?? 0}건
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">합계 금액</Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 24 }, fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>
            {formatAmount(data.summary?.totalAmount ?? 0)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>전체 검색</Typography>
          <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {data.total?.toLocaleString()}건
          </Typography>
        </Paper>
      </Stack>

      {/* 카테고리별 매출 — 날짜/상단 필터 기준. 카드들은 겹칠 수 있어 합계와 더하지 않는다. */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          카테고리별 매출
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          결제 한 건이 키오스크·RF 카드처럼 둘 이상의 관점에 함께 포함될 수 있습니다.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 1 }}>
          {SALES_CATEGORIES.map((item) => {
            const summary = item.key ? data.categorySummary?.[item.key] : data.summary;
            const selected = category === item.key;
            return (
              <Button
                key={item.key || 'all'}
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => handleCategoryChange(item.key)}
                sx={{
                  minHeight: 70,
                  px: 1.25,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography component="span" variant="caption" sx={{ color: selected ? 'inherit' : 'text.secondary' }}>
                  {item.label}
                </Typography>
                <Typography component="span" sx={{ mt: 0.25, fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {(summary?.totalAmount ?? 0).toLocaleString()}원
                </Typography>
                <Typography component="span" variant="caption" sx={{ mt: 0.25, color: selected ? 'inherit' : 'text.secondary' }}>
                  {(summary?.count ?? 0).toLocaleString()}건
                </Typography>
              </Button>
            );
          })}
        </Box>
      </Paper>

      {/* 테이블 — 모바일은 핵심만 (날짜/번호판/금액) */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell align="center" sx={{ width: 60 }}>출처</TableCell>
              <TableCell>날짜/시간</TableCell>
              <TableCell>번호판</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>상품명</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>결제수단</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>카드사</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>위치</TableCell>
              <TableCell align="right">금액</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">조회 중...</TableCell>
              </TableRow>
            ) : data.list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                  해당 기간 결제 내역이 없습니다
                </TableCell>
              </TableRow>
            ) : (
              data.list.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell align="center">
                    <Chip
                      label={SOURCE_LABELS[row.source] ?? '외주'}
                      size="small"
                      color={SOURCE_COLORS[row.source] ?? 'default'}
                      variant={row.source === 'external' ? 'outlined' : 'filled'}
                      sx={tableTagSx(40)}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {new Date(row.date).toLocaleString('ko-KR', {
                      month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.lp || '-'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.productName}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip
                      label={row.payMethod}
                      size="small"
                      color={payMethodColor(row.payMethod)}
                      variant="outlined"
                      sx={tableTagSx(72)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary', display: { xs: 'none', lg: 'table-cell' } }}>{row.issuer}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary', display: { xs: 'none', lg: 'table-cell' } }}>{row.location}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.amount?.toLocaleString()}원
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {data.total > 50 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(data.total / 50)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default SalesPage;
